/**
 * Notification Templates Service (US-16-GR-23)
 *
 * CRUD client for /api/Notifications/Templates (NotificationTemplatesController).
 * All endpoints are Admin-role gated server-side.
 *
 * Aligned to NotificationTemplateEntity (NotificationLibrary.Entities):
 *   Id (Guid), Category (TemplateCategory enum), Channel (TemplateChannel enum),
 *   Subject (string?), BodyTemplate (string), IsActive (bool),
 *   IsAIGhostwritingScaffold (bool), CreatedAt, UpdatedAt, LastUpdatedBy (Guid?)
 *
 * The API has NO global JsonStringEnumConverter, so Category/Channel serialise
 * as INTEGERS both on the wire out and in the request body.
 */

import { authenticatedFetch } from '../api/authenticatedFetch';

// ---------------------------------------------------------------------------
// Enums — numeric values mirror the C# enums exactly
// (Notifications.Models.Enums.NotificationTemplateEnums)
// ---------------------------------------------------------------------------

export enum TemplateCategory {
  TaskDueReminder = 0,
  OverdueAlert = 1,
  PartnerEncouragementDraft = 2,
  PartnerKudosDraft = 3,
  InvestorUpdate = 4,
  BidNotification = 5,
  MilestoneAchievement = 6,
  InvitationPartner = 7,
  InvitationCollaborator = 8,
  InvitationInvestor = 9,
}

export enum TemplateChannel {
  InApp = 0,
  Email = 1,
  SMS = 2,
}

export const TEMPLATE_CATEGORY_LABELS: Record<TemplateCategory, string> = {
  [TemplateCategory.TaskDueReminder]: 'Task Due Reminder',
  [TemplateCategory.OverdueAlert]: 'Overdue Alert',
  [TemplateCategory.PartnerEncouragementDraft]: 'Partner Encouragement Draft',
  [TemplateCategory.PartnerKudosDraft]: 'Partner Kudos Draft',
  [TemplateCategory.InvestorUpdate]: 'Investor Update',
  [TemplateCategory.BidNotification]: 'Bid Notification',
  [TemplateCategory.MilestoneAchievement]: 'Milestone Achievement',
  [TemplateCategory.InvitationPartner]: 'Invitation — Partner',
  [TemplateCategory.InvitationCollaborator]: 'Invitation — Collaborator',
  [TemplateCategory.InvitationInvestor]: 'Invitation — Investor',
};

export const TEMPLATE_CHANNEL_LABELS: Record<TemplateChannel, string> = {
  [TemplateChannel.InApp]: 'In-App',
  [TemplateChannel.Email]: 'Email',
  [TemplateChannel.SMS]: 'SMS',
};

// ---------------------------------------------------------------------------
// DTO — aligned to NotificationTemplateEntity
// ---------------------------------------------------------------------------

export interface NotificationTemplate {
  id: string;
  category: TemplateCategory;
  channel: TemplateChannel;
  subject?: string | null;
  bodyTemplate: string;
  isActive: boolean;
  isAIGhostwritingScaffold: boolean;
  createdAt: string;
  updatedAt: string;
  lastUpdatedBy?: string | null;
}

/** Fields the admin form supplies when creating or editing a template. */
export interface TemplateInput {
  category: TemplateCategory;
  channel: TemplateChannel;
  subject?: string | null;
  bodyTemplate: string;
  isActive: boolean;
  isAIGhostwritingScaffold?: boolean;
}

// Mirrors the per-service base-URL pattern in agentAdminService.ts
// (VITE_SERVICEMANAGER_API_URL / SERVICEMANAGER_BASE): the admin SWA builds
// with VITE_API_URL pointed directly at TaskManager.API, which has no
// `/api/Notifications/*` routes — those live on the Notifications service
// itself. When VITE_NOTIFICATIONS_API_URL is set (all deployed builds),
// requests go straight to that host. Falls back to the relative path for
// local dev (Vite proxy / gateway rewrite).
const NOTIFICATIONS_BASE: string = import.meta.env.VITE_NOTIFICATIONS_API_URL || '';
const BASE = `${NOTIFICATIONS_BASE}/api/Notifications/Templates`;

/**
 * Error carrying the server's unknown-token list from a 400 "Unknown placeholder
 * tokens" validation failure. The Templates page surfaces `unknownTokens` inline.
 */
export class UnknownTokenError extends Error {
  unknownTokens: string[];
  constructor(unknownTokens: string[]) {
    super(
      unknownTokens.length > 0
        ? `Unknown placeholder tokens: ${unknownTokens.join(', ')}`
        : 'Unknown placeholder tokens',
    );
    this.name = 'UnknownTokenError';
    this.unknownTokens = unknownTokens;
  }
}

// ---------------------------------------------------------------------------
// Client-side token validation
// ---------------------------------------------------------------------------

/**
 * Known placeholder tokens the server accepts. Mirrors the server's allow-list so
 * the admin gets immediate feedback before the round-trip. The server remains the
 * source of truth — a 400 with unknownTokens is still surfaced if this drifts.
 * Tokens use {{token}} syntax.
 */
export const KNOWN_TEMPLATE_TOKENS = [
  'userName',
  'firstName',
  'lastName',
  'taskTitle',
  'taskDueDate',
  'boardName',
  'partnerName',
  'inviterName',
  'campaignName',
  'amount',
  'milestoneName',
  'providerName',
  'bidAmount',
  'link',
  'appName',
] as const;

const TOKEN_PATTERN = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;

/**
 * Extract {{token}} placeholders from a body and return any that are not in the
 * known allow-list. Empty result = all tokens recognised.
 */
export function findUnknownTokens(body: string): string[] {
  const known = new Set<string>(KNOWN_TEMPLATE_TOKENS as readonly string[]);
  const seen = new Set<string>();
  const unknown: string[] = [];
  let match: RegExpExecArray | null;
  while ((match = TOKEN_PATTERN.exec(body)) !== null) {
    const token = match[1];
    if (!known.has(token) && !seen.has(token)) {
      seen.add(token);
      unknown.push(token);
    }
  }
  return unknown;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

async function readError(response: Response): Promise<Error> {
  const body = (await response.json().catch(() => null)) as
    | { message?: string; unknownTokens?: string[] }
    | null;
  if (body?.unknownTokens && Array.isArray(body.unknownTokens)) {
    return new UnknownTokenError(body.unknownTokens);
  }
  return new Error(body?.message ?? `HTTP ${response.status}: ${response.statusText}`);
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * GET /api/Notifications/Templates?category=&channel=&isActive=
 * Enum filters are sent as integers (accepted by the [FromQuery] enum binder).
 */
export async function listTemplates(filter: {
  category?: TemplateCategory;
  channel?: TemplateChannel;
  isActive?: boolean;
} = {}): Promise<NotificationTemplate[]> {
  const params = new URLSearchParams();
  if (filter.category !== undefined) params.set('category', String(filter.category));
  if (filter.channel !== undefined) params.set('channel', String(filter.channel));
  if (filter.isActive !== undefined) params.set('isActive', String(filter.isActive));
  const qs = params.toString();

  const response = await authenticatedFetch(`${BASE}${qs ? `?${qs}` : ''}`);
  if (!response.ok) throw await readError(response);
  return response.json() as Promise<NotificationTemplate[]>;
}

/** POST /api/Notifications/Templates */
export async function createTemplate(input: TemplateInput): Promise<NotificationTemplate> {
  const response = await authenticatedFetch(BASE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      category: input.category,
      channel: input.channel,
      subject: input.subject ?? null,
      bodyTemplate: input.bodyTemplate,
      isActive: input.isActive,
      isAIGhostwritingScaffold: input.isAIGhostwritingScaffold ?? false,
    }),
  });
  if (!response.ok) throw await readError(response);
  return response.json() as Promise<NotificationTemplate>;
}

/** PUT /api/Notifications/Templates/{id} */
export async function updateTemplate(
  id: string,
  input: TemplateInput,
): Promise<NotificationTemplate> {
  const response = await authenticatedFetch(`${BASE}/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      id,
      category: input.category,
      channel: input.channel,
      subject: input.subject ?? null,
      bodyTemplate: input.bodyTemplate,
      isActive: input.isActive,
      isAIGhostwritingScaffold: input.isAIGhostwritingScaffold ?? false,
    }),
  });
  if (!response.ok) throw await readError(response);
  return response.json() as Promise<NotificationTemplate>;
}

/**
 * DELETE /api/Notifications/Templates/{id} — soft-delete (deactivate).
 * Returns 204 on success.
 */
export async function deactivateTemplate(id: string): Promise<void> {
  const response = await authenticatedFetch(`${BASE}/${id}`, { method: 'DELETE' });
  if (!response.ok && response.status !== 204) throw await readError(response);
}
